import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  applyPan,
  applyZoom,
  clampViewBox,
  formatViewBox,
  scaleOf,
  type ViewBox,
} from "@/utils/trackMapZoom";

export interface TrackMapControls {
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
  isZoomed: boolean;
  zoomPercent: number;
}

interface UseTrackMapZoomParams {
  svgRef: React.RefObject<SVGSVGElement | null>;
  base: ViewBox;
  /** Change to force the view back to fit (e.g. layout data identity). */
  resetKey?: unknown;
  /**
   * Gate for zoom/pan behavior. When false (the default) the native wheel
   * listener is NOT registered, so consumers that never opted in keep normal
   * page scrolling over the map.
   */
  enabled?: boolean;
}

interface ZoomHandlers {
  onPointerDown: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerMove: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerUp: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerCancel: (e: React.PointerEvent<SVGSVGElement>) => void;
  onDoubleClick: (e: React.MouseEvent<SVGSVGElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<SVGSVGElement>) => void;
}

interface UseTrackMapZoomResult extends TrackMapControls {
  viewBox: string;
  handlers: ZoomHandlers;
}

const BUTTON_ZOOM_FACTOR = 1.5;
const DBLCLICK_ZOOM_FACTOR = 1.9;
const WHEEL_ZOOM_RATE = 1.0018;
const RESET_ANIMATION_MS = 200;

/**
 * Zoom/pan state for the track map SVG via viewBox manipulation.
 *
 * - Buttons/dblclick/keyboard zoom about the viewport center (buttons) or
 *   cursor (dblclick/wheel); drag pans; two-pointer touch pinch zooms about
 *   the pinch midpoint.
 * - Wheel is registered as a NATIVE non-passive listener (React's synthetic
 *   onWheel is passive and cannot preventDefault).
 * - Coordinate conversion needs getScreenCTM, which is unavailable in jsdom
 *   and while the SVG is detached — all pointer/wheel handlers are no-ops
 *   in that case (tests exercise zoomIn/zoomOut/reset directly).
 * - Inert unless `enabled` is true: with the default `enabled: false` no
 *   native wheel listener is attached, so non-opt-in consumers are
 *   completely unaffected (zero behavior change).
 */
export function useTrackMapZoom({
  svgRef,
  base,
  resetKey,
  enabled = false,
}: UseTrackMapZoomParams): UseTrackMapZoomResult {
  const [vb, setVb] = useState<ViewBox>(base);
  const vbRef = useRef(vb);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ d: number; m: { x: number; y: number } } | null>(null);
  const dragLast = useRef<{ x: number; y: number } | null>(null);

  const update = useCallback(
    (next: ViewBox) => {
      const clamped = clampViewBox(next, base);
      vbRef.current = clamped;
      setVb(clamped);
    },
    [base],
  );

  // Fit whenever the underlying layout identity changes.
  useEffect(() => {
    vbRef.current = base;
    setVb(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- base is a stable memo; resetKey is the trigger
  }, [resetKey]);

  const clientToViewBox = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      const svg = svgRef.current;
      if (!svg || typeof svg.getScreenCTM !== "function") return null;
      const ctm = svg.getScreenCTM();
      if (!ctm) return null;
      const pt = svg.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;
      const p = pt.matrixTransform(ctm.inverse());
      return { x: p.x, y: p.y };
    },
    [svgRef],
  );

  const zoomAtCenter = useCallback(
    (factor: number) => {
      update(
        applyZoom(vbRef.current, base, {
          cx: base.x + base.w / 2,
          cy: base.y + base.h / 2,
          factor,
        }),
      );
    },
    [base, update],
  );

  const zoomIn = useCallback(() => zoomAtCenter(BUTTON_ZOOM_FACTOR), [zoomAtCenter]);
  const zoomOut = useCallback(() => zoomAtCenter(1 / BUTTON_ZOOM_FACTOR), [zoomAtCenter]);

  const reset = useCallback(() => {
    const reduceMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion || typeof requestAnimationFrame !== "function") {
      update(base);
      return;
    }
    const from = { ...vbRef.current };
    const start = performance.now();
    const step = (t: number) => {
      const k = Math.min(1, (t - start) / RESET_ANIMATION_MS);
      const eased = 1 - Math.pow(1 - k, 3);
      update({
        x: from.x + (base.x - from.x) * eased,
        y: from.y + (base.y - from.y) * eased,
        w: from.w + (base.w - from.w) * eased,
        h: from.h + (base.h - from.h) * eased,
      });
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [base, update]);

  // Native non-passive wheel zoom (incl. ctrl+wheel / trackpad pinch).
  // Only registered when the consumer opted in — otherwise scrolling over
  // the map must keep its native behavior.
  useEffect(() => {
    if (!enabled) return;
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (e.deltaY === 0) return;
      const p = clientToViewBox(e.clientX, e.clientY);
      if (!p) return;
      update(
        applyZoom(vbRef.current, base, {
          cx: p.x,
          cy: p.y,
          factor: Math.pow(WHEEL_ZOOM_RATE, -e.deltaY),
        }),
      );
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, [enabled, svgRef, base, clientToViewBox, update]);

  const panByClientDelta = useCallback(
    (dxClient: number, dyClient: number) => {
      const svg = svgRef.current;
      if (!svg || svg.clientWidth === 0) return;
      const k = vbRef.current.w / svg.clientWidth;
      update(applyPan(vbRef.current, base, { dx: -dxClient * k, dy: -dyClient * k }));
    },
    [base, svgRef, update],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (e.button !== 0) return;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.current.size === 2) {
        dragLast.current = null;
        const [a, b] = [...pointers.current.values()];
        pinch.current = {
          d: Math.hypot(a.x - b.x, a.y - b.y),
          m: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        };
      } else if (pointers.current.size === 1) {
        dragLast.current = { x: e.clientX, y: e.clientY };
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    },
    [],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!pointers.current.has(e.pointerId)) return;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.current.size === 2 && pinch.current) {
        const [a, b] = [...pointers.current.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        if (pinch.current.d > 0) {
          const p = clientToViewBox(m.x, m.y);
          if (p) {
            update(
              applyZoom(vbRef.current, base, {
                cx: p.x,
                cy: p.y,
                factor: d / pinch.current.d,
              }),
            );
          }
          panByClientDelta(m.x - pinch.current.m.x, m.y - pinch.current.m.y);
        }
        pinch.current = { d, m };
        return;
      }

      if (!dragLast.current) return;
      panByClientDelta(e.clientX - dragLast.current.x, e.clientY - dragLast.current.y);
      dragLast.current = { x: e.clientX, y: e.clientY };
    },
    [base, clientToViewBox, panByClientDelta, update],
  );

  const endPointer = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) {
      dragLast.current = null;
    } else {
      const [a] = [...pointers.current.values()];
      dragLast.current = { x: a.x, y: a.y };
    }
  }, []);

  const onDoubleClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const p = clientToViewBox(e.clientX, e.clientY);
      if (!p) return;
      update(
        applyZoom(vbRef.current, base, {
          cx: p.x,
          cy: p.y,
          factor: e.shiftKey ? 1 / DBLCLICK_ZOOM_FACTOR : DBLCLICK_ZOOM_FACTOR,
        }),
      );
    },
    [base, clientToViewBox, update],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<SVGSVGElement>) => {
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        e.stopPropagation();
        zoomIn();
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        e.stopPropagation();
        zoomOut();
      } else if (e.key === "0") {
        e.preventDefault();
        e.stopPropagation();
        reset();
      }
    },
    [zoomIn, zoomOut, reset],
  );

  const scale = scaleOf(vb, base);

  return {
    viewBox: formatViewBox(vb),
    isZoomed: scale > 1.01,
    zoomPercent: Math.round(scale * 100),
    zoomIn,
    zoomOut,
    reset,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endPointer,
      onPointerCancel: endPointer,
      onDoubleClick,
      onKeyDown,
    },
  };
}
